import { Table, Input, Select, Tag, Card, Button, message, Typography } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import { ApiService } from "api/api.service";
import { ColumnsType } from "antd/es/table";
import { Gateway, TransactionDto } from "dtos/wallet.transaction.dto";
import DashboardLayout from "layout/DashboardLayout";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { TbReload } from "react-icons/tb";
import PageTitle from "../../components/PageTitle";

const { Option } = Select;

export default function TransactionLogs() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const query = useMemo(() => {
    return {
      application_id: params.get("application_id") || undefined,
      creditor_id: params.get("creditor_id") || undefined,
      org_id: params.get("org_id") || undefined,
      wallet_type: params.get("wallet_type") || "",
      type: params.get("type") || undefined,
      status: params.get("status") || undefined,
      search: params.get("search") || "",
      page: Number(params.get("page") || 1),
      limit: Number(params.get("limit") || 10),
    };
  }, [params]);

  const { data, isLoading, refetch, isPending } = ApiService.wallet.useGetTransactionLogs(query);

  const logs = data?.result.data ?? [];
  const pagination = data?.result;
  const wallet = data?.result?.wallet;
  const reconcWallet = data?.result?.reconciliation_wallet;

  const updateParams = (newParams: Record<string, any>) => {
    const updated = new URLSearchParams(params);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value === undefined || value === "" || value === null) {
        updated.delete(key);
      } else {
        updated.set(key, String(value));
      }
    });

    setParams(updated);
  };

  const columns: ColumnsType<TransactionDto> = [
    {
      title: "Txn. Date",
      dataIndex: "processed_at",
      render: (val) => <div className="text-sm text-gray-600">{new Date(val).toLocaleString()}</div>,
    },
    {
      title: "Posting Date",
      dataIndex: "meta.posting_date",
      render: (val, record) => (
        <div className="text-sm text-gray-600">{record.meta.posting_date ? record.meta.posting_date : "-"}</div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (val) => {
        const isCredit = val === "credit";

        return (
          <div className="flex items-center gap-2">
            <div
              className={`p-1.5 rounded-full ${isCredit ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}
            >
              {isCredit ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
            </div>

            <span className={`font-medium ${isCredit ? "text-green-600" : "text-red-600"}`}>{val.toUpperCase()}</span>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (val) => {
        const colorMap: any = {
          succeeded: "green",
          failed: "red",
          pending: "gold",
          processing: "blue",
        };

        return (
          <Tag color={colorMap[val] || "default"} className="rounded-full px-3 py-1 text-xs font-medium">
            {val.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Txn. Amount",
      dataIndex: "amount",
      align: "right",
      render: (val, record) => {
        return <span className={`font-semibold text-green-600`}>${val.toFixed(2)}</span>;
      },
    },
    {
      title: "Starting Balance",
      dataIndex: "starting_balance",
      align: "right",
      render: (val, record) => {
        return <span className={`font-semibold`}>{`$${val.toFixed(2)}`}</span>;
      },
    },
    {
      title: "Ending Balance",
      dataIndex: "ending_balance",
      align: "right",
      render: (val, record) => {
        return <span className={`font-semibold`}>{`$${val.toFixed(2)}`}</span>;
      },
    },
    {
      title: "Reference Txn. ID",
      render: (_, record: any) => {
        const ref = record?.meta?.ref_transaction_id;

        return ref ? (
          <Typography.Text className="text-gray-600 text-sm" ellipsis={{ tooltip: ref }} style={{ maxWidth: 180 }}>
            {ref}
          </Typography.Text>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Description",
      width: 420,
      render: (_, record) => {
        if (!record.description) return "-";

        const [readable, metaStr] = record.description.split("||");

        let meta: any = null;
        try {
          meta = JSON.parse(metaStr);
        } catch {
          meta = null;
        }

        return (
          <div className="flex flex-col gap-1">
            {/* Full readable text */}
            <Typography.Text className="text-gray-700 text-sm" ellipsis={{ tooltip: readable }}>
              {readable}
            </Typography.Text>

            {/* Anchors */}
            {(meta?.from_route || meta?.to_route) && (
              <div className="text-xs flex gap-2">
                {meta?.from_route && (
                  <a className="text-blue-600 hover:underline" onClick={() => window.open(meta.from_route, "_blank")}>
                    {meta.from || "from"}
                  </a>
                )}

                {meta?.from_route && meta?.to_route && <span>→</span>}

                {meta?.to_route && (
                  <a className="text-blue-600 hover:underline" onClick={() => window.open(meta.to_route, "_blank")}>
                    {meta.to || "to"}
                  </a>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Payment Method",
      dataIndex: "gateway",
      render: (val) => {
        const map: any = {
          [Gateway.STRIPE]: "blue",
          [Gateway.SEAMLESSCHEX]: "purple",
          [Gateway.SEAMLESSCHEX_MERCHANT]: "cyan",
          [Gateway.PAYNOTE]: "orange",
          [Gateway.MANUALLY_MARKED]: "green",
          [Gateway.AUTOMATED_DISTRIBUTION]: "red",
        };

        return (
          <Tag color={map[val] || "default"} className="rounded-full px-3 py-1 text-xs">
            {val.replace("_", " ").toUpperCase()}
          </Tag>
        );
      },
    },
  ];

  return (
    <DashboardLayout
      headerRight={
        <div className="flex gap-2 items-center">
          <Button
            type="primary"
            icon={<TbReload />}
            disabled={isPending}
            onClick={() => {
              refetch();
              message.success("Logs refetched.");
            }}
          >
            Refresh
          </Button>
        </div>
      }
    >
      <PageTitle title="Transaction Logs" pageTitle={[{ title: "Transaction Logs" }]} />
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">Transaction Logs</h2>
            <p className="text-sm text-gray-500">Monitor all wallet activity and transactions</p>
          </div>
        </div>

        <Card className="rounded-2xl border shadow-sm">
          <div className="flex gap-3 flex-wrap items-center">
            <Input
              placeholder="Search reference or error..."
              value={query.search}
              onChange={(e) => updateParams({ search: e.target.value, page: 1 })}
              className="w-72 rounded-lg"
            />

            <Select
              allowClear
              placeholder="Type"
              value={query.type}
              onChange={(val) => updateParams({ type: val, page: 1 })}
              className="w-40"
            >
              <Option value="credit">Credit</Option>
              <Option value="debit">Debit</Option>
            </Select>

            <Select
              allowClear
              placeholder="Status"
              value={query.status}
              onChange={(val) => updateParams({ status: val, page: 1 })}
              className="w-40"
            >
              <Option value="succeeded">Succeeded</Option>
              <Option value="failed">Failed</Option>
              <Option value="pending">Pending</Option>
              <Option value="processing">Processing</Option>
            </Select>
          </div>
        </Card>

        {wallet && (
          <Card className="rounded-2xl border shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-xl text-gray-500">Wallet</div>
                <div className="text-2xl text-gray-800">
                  {`VAN: ${wallet.van ? `****${wallet.van.slice(-4)}` : "-"}`}
                </div>
                {/* <div className="text-lg font-semibold text-gray-800">
                  {wallet.wallet_type.replace(/_/g, " ").toUpperCase()}
                </div> */}
              </div>

              <div className="text-right">
                <div className="text-sm text-gray-500">Last Recorded Balance</div>
                <div className="text-xl font-bold text-green-600">${Number(wallet.balance ?? 0).toFixed(2)}</div>
              </div>
            </div>
          </Card>
        )}

        {reconcWallet && (
          <Card className="rounded-2xl border shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-xl text-gray-500">Reconciliation Wallet</div>
                <div className="text-2xl text-gray-800">
                  {`VAN: ${reconcWallet.van ? `****${reconcWallet.van.slice(-4)}` : "-"}`}
                </div>
                {/* <div className="text-lg font-semibold text-gray-800">
                  {wallet.wallet_type.replace(/_/g, " ").toUpperCase()}
                </div> */}
              </div>

              <div className="text-right">
                <div className="text-sm text-gray-500">Last Recorded Balance</div>
                <div className="text-xl font-bold text-red-600">${Number(reconcWallet.balance ?? 0).toFixed(2)}</div>
              </div>
            </div>
          </Card>
        )}

        <Card className="rounded-2xl border shadow-sm">
          <Table
            scroll={{ y: 400, x: "max-content" }}
            rowKey="_id"
            loading={isLoading}
            dataSource={logs}
            columns={columns}
            pagination={{
              current: pagination?.page,
              pageSize: pagination?.limit,
              total: pagination?.totalObjects,
              showSizeChanger: true,
              showTotal: (total) => `${total} transactions`,
              onChange: (page, limit) => {
                updateParams({ page, limit });
              },
            }}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
}
